import type {
  AssessmentPatientContext,
  AssessmentResultV2,
  AssessmentSharedContext,
} from '../types';
import type { AssessmentV2TestKey } from '../registry/assessmentRegistry';
import {
  buildClinicalReferenceDecision,
  getCurrentAssessmentTestVersion,
} from '../references/clinicalReferenceRegistry';
import { getACTSeverityLabel } from '../renderers/ACTResultRenderer';
import { getAQLQSeverityLabel } from '../renderers/AQLQResultRenderer';
import { getCATSeverityLabel } from '../renderers/CATResultRenderer';
import { getHADSOverallSeverityLabel } from '../renderers/HADSResultRenderer';
import { getLCADLSeverityLabel } from '../renderers/LCADLResultRenderer';
import { getMLHFQSeverityLabel } from '../renderers/MLHFQResultRenderer';
import { getPFSDQSeverityLabel } from '../renderers/PFSDQMResultRenderer';
import { getPSQISeverityLabel } from '../renderers/PSQIResultRenderer';
import { getSAQOverallScore, getSAQSeverityLabel } from '../renderers/SAQResultRenderer';
import { getSGRQSeverityLabel } from '../renderers/SGRQResultRenderer';
import { getMMRCSeverityLabel } from '../renderers/MMRCResultRenderer';
import type { ACTResultPayload } from '../tests/ACTTest';
import type { AQLQResultPayload } from '../tests/AQLQTest';
import type { CATResultPayload } from '../tests/CATTest';
import type { HADSResultPayload } from '../tests/HADSTest';
import type { LCADLResultPayload } from '../tests/LCADLTest';
import type { MLHFQResultPayload } from '../tests/MLHFQTest';
import type { MMRCResultPayload } from '../tests/MMRCTest';
import type { PFSDQMResultPayload } from '../tests/PFSDQMTest';
import type { PSQIResultPayload } from '../tests/PSQITest';
import type { SAQResultPayload } from '../tests/SAQTest';
import type { SGRQResultPayload } from '../tests/SGRQTest';
import { normalizePatientHeightCm, normalizePatientWeightKg } from '../utils/patientMetrics';

type QuestionnaireAssessmentTestKey =
  | 'CAT'
  | 'ACT'
  | 'mMRC'
  | 'PFSDQ-M'
  | 'HADS'
  | 'PSQI'
  | 'AQLQ'
  | 'SGRQ'
  | 'LCADL'
  | 'MLHFQ'
  | 'SAQ';

type BuildQuestionnaireResultInputParams = {
  sessionId: string;
  clientId: string;
  patientId: string;
  testKey: QuestionnaireAssessmentTestKey;
  rawResult: unknown;
  sharedContext: AssessmentSharedContext;
  groupIndex: number;
  groupTotal: number;
  status: AssessmentResultV2['status'];
  notes?: string | null;
  patientNameSnapshot: string;
  patientMedicalRecordSnapshot?: string | null;
  professionalUserId?: string | null;
  professionalNameSnapshot?: string | null;
  professionalRegistrationSnapshot?: string | null;
  professionalRoleSnapshot?: string | null;
  createdByUserId?: string | null;
};

export const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

export const calculateAgeAtDate = (birthDate?: string | null, referenceDate?: string | null): number | null => {
  if (!birthDate || !referenceDate) return null;

  const birth = new Date(birthDate);
  const reference = new Date(referenceDate);

  if (Number.isNaN(birth.getTime()) || Number.isNaN(reference.getTime())) {
    return null;
  }

  let age = reference.getFullYear() - birth.getFullYear();
  const monthDelta = reference.getMonth() - birth.getMonth();

  if (monthDelta < 0 || (monthDelta === 0 && reference.getDate() < birth.getDate())) {
    age -= 1;
  }

  return age >= 0 ? age : null;
};

export const buildQuestionnaireSharedContext = (params: {
  assessmentDateIso: string;
  patientContext: Omit<AssessmentPatientContext, 'ageYears'>;
  professionalContext: AssessmentSharedContext['professionalContext'];
}): AssessmentSharedContext => {
  const patientAgeAtAssessment = calculateAgeAtDate(params.patientContext.birthDate, params.assessmentDateIso);

  return {
    professionalRegistration: params.professionalContext.registration,
    professionalRole: params.professionalContext.role,
    assessmentDateIso: params.assessmentDateIso,
    patientAgeAtAssessment,
    patientContext: {
      ...params.patientContext,
      ageYears: patientAgeAtAssessment,
      weightKg: normalizePatientWeightKg(params.patientContext.weightKg),
      heightCm: normalizePatientHeightCm(params.patientContext.heightCm),
    },
    professionalContext: params.professionalContext,
  };
};

export const resolvePatientContextForQuestionnaire = (
  rawResult: unknown,
  fallback: AssessmentPatientContext,
): AssessmentPatientContext => {
  const override =
    rawResult && typeof rawResult === 'object' && (rawResult as Record<string, unknown>).patientContext
      ? ((rawResult as Record<string, unknown>).patientContext as Record<string, unknown>)
      : null;

  if (!override || typeof override !== 'object') {
    return fallback;
  }

  return {
    birthDate: typeof override.birthDate === 'string' ? override.birthDate : fallback.birthDate,
    ageYears: toNullableNumber(override.ageYears) ?? fallback.ageYears,
    sex: typeof override.sex === 'string' ? override.sex : fallback.sex,
    weightKg: normalizePatientWeightKg(override.weightKg) ?? fallback.weightKg,
    heightCm: normalizePatientHeightCm(override.heightCm) ?? fallback.heightCm,
    identifier: typeof override.identifier === 'string' ? override.identifier : fallback.identifier,
  };
};

const buildQuestionnaireResultContent = (
  testKey: QuestionnaireAssessmentTestKey,
  rawResult: unknown,
  sharedContext: AssessmentSharedContext,
) => {
  const professionalContext = {
    name: sharedContext.professionalContext.name || 'Profissional',
    registration: sharedContext.professionalRegistration,
    role: sharedContext.professionalRole,
    userId: sharedContext.professionalContext.userId ?? null,
  };

  switch (testKey) {
    case 'CAT': {
      const catResult = rawResult as CATResultPayload;
      const total =
        typeof catResult.total === 'number'
          ? catResult.total
          : Array.isArray(catResult.answers)
            ? catResult.answers.reduce((sum, value) => sum + value, 0)
            : 0;

      return {
        summaryJson: {
          total,
          severity: getCATSeverityLabel(total),
          answeredItems: Array.isArray(catResult.answers) ? catResult.answers.length : 0,
          patientAgeYears: sharedContext.patientAgeAtAssessment,
          patientSex: sharedContext.patientContext.sex,
          patientWeightKg: sharedContext.patientContext.weightKg,
          patientHeightCm: sharedContext.patientContext.heightCm,
        },
        payloadJson: {
          answers: Array.isArray(catResult.answers) ? catResult.answers : [],
          total,
          finishedAt: catResult.finishedAt || new Date().toISOString(),
          patientContext: sharedContext.patientContext,
          professionalContext,
        },
        assetsJson: {},
      };
    }
    case 'ACT': {
      const actResult = rawResult as ACTResultPayload;
      const total =
        typeof actResult.total === 'number'
          ? actResult.total
          : Array.isArray(actResult.answers)
            ? actResult.answers.reduce((sum, value) => sum + (typeof value === 'number' ? value : 0), 0)
            : 0;
      const classification = getACTSeverityLabel(total);

      return {
        summaryJson: {
          total,
          classification,
          severity: classification,
          answeredItems: Array.isArray(actResult.answers) ? actResult.answers.length : 0,
          patientAgeYears: sharedContext.patientAgeAtAssessment,
          patientSex: sharedContext.patientContext.sex,
          patientWeightKg: sharedContext.patientContext.weightKg,
          patientHeightCm: sharedContext.patientContext.heightCm,
        },
        payloadJson: {
          ...actResult,
          total,
          classification,
          patientContext: sharedContext.patientContext,
          professionalContext,
        },
        assetsJson: {},
      };
    }
    case 'mMRC': {
      const mmrcResult = rawResult as MMRCResultPayload;
      return {
        summaryJson: {
          grade: mmrcResult.grade,
          severity: getMMRCSeverityLabel(mmrcResult.grade),
          description: mmrcResult.description,
          patientAgeYears: sharedContext.patientAgeAtAssessment,
          patientSex: sharedContext.patientContext.sex,
          patientWeightKg: sharedContext.patientContext.weightKg,
          patientHeightCm: sharedContext.patientContext.heightCm,
        },
        payloadJson: {
          grade: mmrcResult.grade,
          description: mmrcResult.description,
          finishedAt: mmrcResult.finishedAt || new Date().toISOString(),
          patientContext: sharedContext.patientContext,
          professionalContext,
        },
        assetsJson: {},
      };
    }
    case 'HADS': {
      const hadsResult = rawResult as HADSResultPayload;
      const overallSeverity = getHADSOverallSeverityLabel(hadsResult.anxietyScore, hadsResult.depressionScore);

      return {
        summaryJson: {
          anxietyScore: hadsResult.anxietyScore,
          depressionScore: hadsResult.depressionScore,
          anxietyClassification:
            hadsResult.anxietyScore <= 7 ? 'Normal' : hadsResult.anxietyScore <= 10 ? 'Limítrofe' : 'Anormal',
          depressionClassification:
            hadsResult.depressionScore <= 7 ? 'Normal' : hadsResult.depressionScore <= 10 ? 'Limítrofe' : 'Anormal',
          severity: overallSeverity,
          answeredItems: Array.isArray(hadsResult.answers) ? hadsResult.answers.length : 0,
          patientAgeYears: sharedContext.patientAgeAtAssessment,
          patientSex: sharedContext.patientContext.sex,
          patientWeightKg: sharedContext.patientContext.weightKg,
          patientHeightCm: sharedContext.patientContext.heightCm,
        },
        payloadJson: {
          answers: Array.isArray(hadsResult.answers) ? hadsResult.answers : [],
          anxiety: hadsResult.anxiety,
          depression: hadsResult.depression,
          anxietyScore: hadsResult.anxietyScore,
          depressionScore: hadsResult.depressionScore,
          finishedAt: hadsResult.finishedAt || new Date().toISOString(),
          patientContext: sharedContext.patientContext,
          professionalContext,
        },
        assetsJson: {},
      };
    }
    case 'PSQI': {
      const psqiResult = rawResult as PSQIResultPayload;
      const severity = getPSQISeverityLabel(psqiResult.total);

      return {
        summaryJson: {
          total: psqiResult.total,
          severity,
          classification: severity,
          sleepEfficiencyPercent: psqiResult.sleepEfficiencyPercent,
          timeInBedHours: psqiResult.timeInBedHours,
          sleepLatencyMinutes: psqiResult.sleepLatencyMinutes,
          componentScores: psqiResult.components,
          patientAgeYears: sharedContext.patientAgeAtAssessment,
          patientSex: sharedContext.patientContext.sex,
          patientWeightKg: sharedContext.patientContext.weightKg,
          patientHeightCm: sharedContext.patientContext.heightCm,
        },
        payloadJson: {
          ...psqiResult,
          patientContext: sharedContext.patientContext,
          professionalContext,
        },
        assetsJson: {},
      };
    }
    case 'AQLQ': {
      const aqlqResult = rawResult as AQLQResultPayload;
      const severity = getAQLQSeverityLabel(aqlqResult.total);
      return {
        summaryJson: {
          total: aqlqResult.total,
          severity,
          answeredCount: aqlqResult.answeredCount,
          symptomsScore: aqlqResult.domainScores.symptoms.score,
          emotionalFunctionScore: aqlqResult.domainScores.emotionalFunction.score,
          activityLimitationScore: aqlqResult.domainScores.activityLimitation.score,
          environmentalStimuliScore: aqlqResult.domainScores.environmentalStimuli.score,
          sourceMissingItems: aqlqResult.sourceMissingItems,
          patientAgeYears: sharedContext.patientAgeAtAssessment,
          patientSex: sharedContext.patientContext.sex,
          patientWeightKg: sharedContext.patientContext.weightKg,
          patientHeightCm: sharedContext.patientContext.heightCm,
        },
        payloadJson: {
          ...aqlqResult,
          patientContext: sharedContext.patientContext,
          professionalContext,
        },
        assetsJson: {},
      };
    }
    case 'LCADL': {
      const lcadlResult = rawResult as LCADLResultPayload;
      const severity = getLCADLSeverityLabel(lcadlResult.percentage);
      return {
        summaryJson: {
          totalScore: lcadlResult.totalScore,
          maxScore: lcadlResult.maxScore,
          percentage: lcadlResult.percentage,
          severity,
          patientAgeYears: sharedContext.patientAgeAtAssessment,
          patientSex: sharedContext.patientContext.sex,
          patientWeightKg: sharedContext.patientContext.weightKg,
          patientHeightCm: sharedContext.patientContext.heightCm,
        },
        payloadJson: {
          ...lcadlResult,
          patientContext: sharedContext.patientContext,
          professionalContext,
        },
        assetsJson: {},
      };
    }
    case 'MLHFQ': {
      const mlhfqResult = rawResult as MLHFQResultPayload;
      const severity = getMLHFQSeverityLabel(mlhfqResult.total);
      return {
        summaryJson: {
          total: mlhfqResult.total,
          maxScore: mlhfqResult.maxScore,
          physicalScore: mlhfqResult.physical.score,
          emotionalScore: mlhfqResult.emotional.score,
          otherScore: mlhfqResult.other.score,
          severity,
          answeredItems: Array.isArray(mlhfqResult.answers) ? mlhfqResult.answers.length : 0,
          patientAgeYears: sharedContext.patientAgeAtAssessment,
          patientSex: sharedContext.patientContext.sex,
          patientWeightKg: sharedContext.patientContext.weightKg,
          patientHeightCm: sharedContext.patientContext.heightCm,
        },
        payloadJson: {
          ...mlhfqResult,
          patientContext: sharedContext.patientContext,
          professionalContext,
        },
        assetsJson: {},
      };
    }
    case 'PFSDQ-M': {
      const pfsdqResult = rawResult as PFSDQMResultPayload;
      const severity = getPFSDQSeverityLabel(pfsdqResult.total);
      return {
        summaryJson: {
          total: pfsdqResult.total,
          dyspneaScore: pfsdqResult.dyspnea.score,
          fatigueScore: pfsdqResult.fatigue.score,
          changeScore: pfsdqResult.change.score,
          dyspneaAnswered: pfsdqResult.manualCounts.dyspneaAnswered,
          fatigueAnswered: pfsdqResult.manualCounts.fatigueAnswered,
          changeAnswered: pfsdqResult.manualCounts.changeAnswered,
          severity,
          patientAgeYears: sharedContext.patientAgeAtAssessment,
          patientSex: sharedContext.patientContext.sex,
          patientWeightKg: sharedContext.patientContext.weightKg,
          patientHeightCm: sharedContext.patientContext.heightCm,
        },
        payloadJson: {
          ...pfsdqResult,
          patientContext: sharedContext.patientContext,
          professionalContext,
        },
        assetsJson: {},
      };
    }
    case 'SAQ': {
      const saqResult = rawResult as SAQResultPayload;
      const overallScore = getSAQOverallScore(saqResult.domainScores);
      const severity = getSAQSeverityLabel(overallScore);

      return {
        summaryJson: {
          overallScore,
          severity,
          physicalLimitation: saqResult.domainScores.physicalLimitation,
          anginaStability: saqResult.domainScores.anginaStability,
          anginaFrequency: saqResult.domainScores.anginaFrequency,
          treatmentSatisfaction: saqResult.domainScores.treatmentSatisfaction,
          qualityOfLife: saqResult.domainScores.qualityOfLife,
          patientAgeYears: sharedContext.patientAgeAtAssessment,
          patientSex: sharedContext.patientContext.sex,
          patientWeightKg: sharedContext.patientContext.weightKg,
          patientHeightCm: sharedContext.patientContext.heightCm,
        },
        payloadJson: {
          ...saqResult,
          patientContext: sharedContext.patientContext,
          professionalContext,
        },
        assetsJson: {},
      };
    }
    case 'SGRQ': {
      const sgrqResult = rawResult as SGRQResultPayload;
      const severity = getSGRQSeverityLabel(sgrqResult.total);
      return {
        summaryJson: {
          total: sgrqResult.total,
          symptoms: sgrqResult.symptoms,
          activity: sgrqResult.activity,
          impact: sgrqResult.impact,
          severity,
          patientAgeYears: sharedContext.patientAgeAtAssessment,
          patientSex: sharedContext.patientContext.sex,
          patientWeightKg: sharedContext.patientContext.weightKg,
          patientHeightCm: sharedContext.patientContext.heightCm,
        },
        payloadJson: {
          ...sgrqResult,
          patientContext: sharedContext.patientContext,
          professionalContext,
        },
        assetsJson: {},
      };
    }
  }
};

export const buildQuestionnaireAssessmentResultInput = (
  params: BuildQuestionnaireResultInputParams,
) => {
  const content = buildQuestionnaireResultContent(params.testKey, params.rawResult, params.sharedContext);
  const effectivePatientContext = resolvePatientContextForQuestionnaire(params.rawResult, params.sharedContext.patientContext);
  const testVersion = getCurrentAssessmentTestVersion(params.testKey);
  const baseSummaryJson =
    content.summaryJson && typeof content.summaryJson === 'object' && !Array.isArray(content.summaryJson)
      ? {
          ...content.summaryJson,
          patientAgeYears: params.sharedContext.patientAgeAtAssessment,
          patientSex: effectivePatientContext.sex,
          patientWeightKg: effectivePatientContext.weightKg,
          patientHeightCm: effectivePatientContext.heightCm,
        }
      : content.summaryJson;
  const basePayloadJson =
    content.payloadJson && typeof content.payloadJson === 'object' && !Array.isArray(content.payloadJson)
      ? {
          ...content.payloadJson,
          patientContext: effectivePatientContext,
        }
      : content.payloadJson;
  const clinicalReference = buildClinicalReferenceDecision({
    testKey: params.testKey,
    testVersion,
    payload: basePayloadJson,
    evaluatedAt: params.sharedContext.assessmentDateIso,
  });
  const summaryJson = {
    ...baseSummaryJson,
    instrumentVersion: clinicalReference.instrumentVersion,
    clinicalReferenceEngineVersion: clinicalReference.engineVersion,
  };
  const payloadJson = {
    ...basePayloadJson,
    instrumentVersion: clinicalReference.instrumentVersion,
    clinicalReference,
  };

  return {
    sessionId: params.sessionId,
    clientId: params.clientId,
    patientId: params.patientId,
    testKey: params.testKey as AssessmentV2TestKey,
    testVersion,
    assessmentDate: params.sharedContext.assessmentDateIso,
    groupIndex: params.groupIndex,
    groupTotal: params.groupTotal,
    status: params.status,
    notes: params.notes || null,
    patientNameSnapshot: params.patientNameSnapshot,
    patientMedicalRecordSnapshot: params.patientMedicalRecordSnapshot || null,
    patientBirthDateSnapshot: effectivePatientContext.birthDate,
    patientSexSnapshot: effectivePatientContext.sex,
    patientWeightSnapshot: effectivePatientContext.weightKg,
    patientHeightSnapshot: effectivePatientContext.heightCm,
    professionalUserId: params.professionalUserId || null,
    professionalNameSnapshot: params.professionalNameSnapshot || 'Profissional',
    professionalRegistrationSnapshot: params.professionalRegistrationSnapshot || null,
    professionalRoleSnapshot: params.professionalRoleSnapshot || null,
    summaryJson,
    payloadJson,
    assetsJson: content.assetsJson,
    legacySourceRecordId: null,
    createdByUserId: params.createdByUserId || null,
  };
};
